import { createContext, useState, ReactNode } from 'react';
import { HaveYouMetDialogProps } from '@/components/features/dialogs/HaveYouMetDialog';
import { InitMatchDialogProps } from '@/components/features/dialogs/InitMatchDialog';
import { MatchFeedbackDialogProps } from '@/components/features/dialogs/MatchFeedbackDialog';
import { MessageSentDialogProps } from '@/components/features/dialogs/MessageSentDialog';
import { NotificationOptInDialogProps } from '@/components/features/dialogs/NotificationOptInDialog';
import { PhotoUploadDialogProps } from '@/components/features/dialogs/PhotoUploadDialog';
import { ProfileDialogProps } from '@/components/features/dialogs/ProfileDialog';
import { RefugeeChooseCanadaAlertDialogProps } from '@/components/features/dialogs/RefugeeChooseCanadaAlertDialog';
import { ReportUserDialogProps } from '@/components/features/dialogs/ReportUserDialog';
import { ResettlementNotAppliedToCanadaDialogProps } from '@/components/features/dialogs/ResettlementNotAppliedToCanadaDialog';
import { SignupDialogProps } from '@/components/features/dialogs/SignupDialog';
import { UnsavedChangesDialogProps } from '@/components/features/dialogs/UnsavedChangesDialog';
import { WithdrawMatchDialogProps } from '@/components/features/dialogs/WithdrawMatchDialog';
import { pushAnalyticsEvent } from '@/lib/analyticsClientSide';
// We use a file name convention to identify the dialogs
// e.g. MessageSentDialog.tsx -> 'message-sent'
export const DIALOGS = {
  signup: { props: {} as SignupDialogProps },
  'match-feedback': { props: {} as MatchFeedbackDialogProps },
  'contact-haven': { props: null },
  'email-verification': { props: null },
  'photo-upload': { props: {} as PhotoUploadDialogProps },
  'init-match': { props: {} as InitMatchDialogProps },
  'message-sent': { props: {} as MessageSentDialogProps },
  'message-request-verification': { props: null },
  'withdraw-match': { props: {} as WithdrawMatchDialogProps },
  'report-user': { props: {} as ReportUserDialogProps },
  'resettlement-not-applied-to-canada': {
    props: {} as ResettlementNotAppliedToCanadaDialogProps,
  },
  'refugee-choose-canada-alert': {
    props: {} as RefugeeChooseCanadaAlertDialogProps,
  },
  profile: { props: {} as ProfileDialogProps },
  'have-you-met': { props: {} as HaveYouMetDialogProps },
  'unsaved-changes': { props: {} as UnsavedChangesDialogProps },
  'notification-opt-in': { props: {} as NotificationOptInDialogProps },
  'user-availability-nudge': { props: null },
} as const;

export type DialogKey = keyof typeof DIALOGS;

// Add a type for dialog props
export type DialogProps = {
  [K in DialogKey]: (typeof DIALOGS)[K]['props'];
};

interface DialogContextProps {
  openDialog: <K extends DialogKey>(
    dialog: K,
    ...args: (typeof DIALOGS)[K]['props'] extends null
      ? []
      : [(typeof DIALOGS)[K]['props']]
  ) => void;
  closeDialog: (dialog: DialogKey) => void;
  isOpen: (dialog: DialogKey) => boolean;
  getDialogProps: <K extends DialogKey>(
    dialog: K
  ) => (typeof DIALOGS)[K]['props'] | undefined;
  openDialogsCount: number;
  incrementOpenDialogs: () => void;
  decrementOpenDialogs: () => void;
}

export const DialogContext = createContext<DialogContextProps>(
  {} as DialogContextProps
);

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [dialogStates, setDialogStates] = useState<Record<DialogKey, boolean>>(
    Object.fromEntries(
      Object.keys(DIALOGS).map((key) => [key, false])
    ) as Record<DialogKey, boolean>
  );

  // Add state for dialog props
  const [dialogProps, setDialogProps] = useState<Partial<DialogProps>>({});

  const [openDialogsCount, setOpenDialogsCount] = useState(0);

  const incrementOpenDialogs = () => {
    setOpenDialogsCount((count) => {
      const newCount = count + 1;
      return newCount;
    });
  };

  const decrementOpenDialogs = () => {
    setOpenDialogsCount((count) => {
      const newCount = Math.max(0, count - 1);
      return newCount;
    });
  };

  const openDialog = <K extends DialogKey>(
    dialog: K,
    ...args: (typeof DIALOGS)[K]['props'] extends null
      ? []
      : [(typeof DIALOGS)[K]['props']]
  ) => {
    setDialogStates((prev) => ({ ...prev, [dialog]: true }));
    setDialogProps((prev) => ({ ...prev, [dialog]: args[0] }));
    const params = Object.entries(args[0] ?? {}).reduce(
      (acc, [key, value]) => {
        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string | number | boolean>
    );
    pushAnalyticsEvent(`${dialog}-dialog-opened`, params);
  };

  const closeDialog = (dialog: DialogKey) => {
    setDialogStates((prev) => ({ ...prev, [dialog]: false }));
    // Optionally clear props when closing
    setDialogProps((prev) => {
      const newProps = { ...prev };
      delete newProps[dialog];
      return newProps;
    });
  };

  const isOpen = (dialog: DialogKey) => dialogStates[dialog];
  const getDialogProps = <K extends DialogKey>(dialog: K) =>
    dialogProps[dialog];

  return (
    <DialogContext
      value={{
        openDialog,
        closeDialog,
        isOpen,
        getDialogProps,
        openDialogsCount,
        incrementOpenDialogs,
        decrementOpenDialogs,
      }}
    >
      {children}
    </DialogContext>
  );
};
